import type { ComboboxItem, SelectProps as MantineSelectProps } from '@mantine/core';

/** v6-compat creatable API, re-implemented on top of Mantine v7 (shared by the creatable compositions). */
export interface CreatableSelectProps {
  /** Allow creating a new option from the current search query. */
  creatable?: boolean;
  /** Label for the synthetic "create" option (e.g. `` (query) => `+ Create ${query}` ``). */
  getCreateLabel?: (query: string) => string;
  /**
   * Called when the "create" option is picked. Return the created item (or its
   * value) to select it; return nothing to select the raw query.
   */
  onCreate?: (query: string) => ComboboxItem | string | null | undefined;
}

/**
 * Sentinel option value used by the `creatable` re-implementation. Mantine v7
 * removed the v6 `creatable` prop from Select/MultiSelect; the compositions
 * append a synthetic "create" option with this value and translate its
 * selection into an `onCreate` call (see `creatable-select` /
 * `creatable-multi-select` in `@highlight-cp/core-ui-compositions`).
 */
export const CREATE_OPTION_VALUE = '__core-ui-create-option__';

type ComboboxData = MantineSelectProps['data'];

const matchesQuery = (candidate: string | undefined, query: string) =>
  candidate?.toLowerCase() === query;

/**
 * Appends the synthetic "create" option when the search query has no exact
 * (case-insensitive) match in `data` — mirrors Mantine v6's default
 * `shouldCreate` behavior.
 */
export function withCreateOption(
  data: ComboboxData,
  query: string,
  getCreateLabel?: (query: string) => string,
): ComboboxData {
  const trimmed = query.trim();
  if (!trimmed) {
    return data;
  }

  const lowerQuery = trimmed.toLowerCase();
  const exists = (data ?? []).some((entry) => {
    if (typeof entry === 'string') {
      return matchesQuery(entry, lowerQuery);
    }
    if ('items' in entry) {
      return entry.items.some((item) =>
        typeof item === 'string'
          ? matchesQuery(item, lowerQuery)
          : matchesQuery(item.value, lowerQuery) ||
            ('label' in item && matchesQuery(item.label, lowerQuery)),
      );
    }
    return matchesQuery(entry.value, lowerQuery) || matchesQuery(entry.label, lowerQuery);
  });

  if (exists) {
    return data;
  }

  return [
    ...(data ?? []),
    { value: CREATE_OPTION_VALUE, label: getCreateLabel?.(trimmed) ?? trimmed },
  ];
}

/**
 * Resolves the concrete option value for a create action from the consumer's
 * `onCreate` return (v6 allowed returning the created item, its value, or
 * nothing — in which case the raw query becomes the value).
 */
export function resolveCreatedValue(
  created: { value: string } | string | null | undefined,
  query: string,
): string {
  if (typeof created === 'string') {
    return created;
  }
  return created?.value ?? query;
}
